import { OracleConversationMarkdown } from '@modules/oracle/components/OracleConversationMarkdown';
import { OraclePrompt } from '@modules/oracle/store/types';
import { trackWisdomEvents } from '@modules/oracle/utils';
import { Box, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { OracleConversationResponseFooter } from './OracleConversationResponseFooter';

interface OracleSearchResponseProps {
  prompt: OraclePrompt;
  isReadOnly?: boolean;
  onContentLoaded?: () => void;
  answerMarkdown: string;
}

export function OracleConversationResponse({
  prompt,
  isReadOnly,
  onContentLoaded,
  answerMarkdown,
}: OracleSearchResponseProps) {
  const steps = prompt?.assistantResponse?.steps;
  const hasTrackedLastToken = useRef(false);
  const isFirstTokenStreamedTracked = useRef(false);

  useEffect(() => {
    onContentLoaded?.();
  }, [steps, answerMarkdown, onContentLoaded]);

  // Used state to preserve the initial value of endOfResponse to determine if the typing animation should be skipped
  const [shouldSkipTypingAnimation] = useState(!!prompt.endOfResponse);

  const onCopy = () => {
    const selectedText = window.getSelection()?.toString();

    trackWisdomEvents({
      eventName: 'Wisdom - Manually Copy Response',
      response: selectedText,
      copiedFrom: 'chat view',
    });
  };

  useEffect(() => {
    if (!answerMarkdown || isFirstTokenStreamedTracked.current) {
      return;
    }

    trackWisdomEvents({
      eventName: 'Wisdom - First Answer token Streamed',
      searchText: prompt.question,
      response: answerMarkdown,
    });
    isFirstTokenStreamedTracked.current = true;
  }, [answerMarkdown, prompt.question]);

  useEffect(() => {
    if (!prompt.endOfResponse || hasTrackedLastToken.current) {
      return;
    }

    trackWisdomEvents({
      eventName: 'Wisdom - Last Answer token Streamed',
      searchText: prompt.question,
      response: answerMarkdown,
    });
    hasTrackedLastToken.current = true;
  }, [prompt.endOfResponse, answerMarkdown, prompt.question]);

  return (
    <Stack direction="column" spacing={2}>
      {answerMarkdown ? (
        <Box onCopy={onCopy}>
          <OracleConversationMarkdown
            promptID={prompt.id}
            markdown={answerMarkdown}
            isLoading={!prompt.endOfResponse}
            shouldSkipTypingAnimation={shouldSkipTypingAnimation}
          />
        </Box>
      ) : null}
      <OracleConversationResponseFooter
        prompt={prompt}
        answerMarkdown={answerMarkdown}
        isReadOnly={isReadOnly}
      />
    </Stack>
  );
}
